import { useState } from 'react';
import { useAddComment } from '../api/tasks';
import type { Task } from '../lib/types';

interface Props {
  task: Task;
  projectSlug: string;
}

/**
 * Comment input with the continuation toggle.
 *
 * Behavior:
 *   - On a non-completed task: simple comment box. No toggle shown.
 *   - On a completed task: shows "Create follow-up task on the same
 *     branch" checkbox. Default ON. User can uncheck to leave just a
 *     plain comment without spawning more work.
 */
export default function CommentComposer({ task, projectSlug }: Props) {
  const [body, setBody] = useState('');
  const isCompleted = task.status === 'completed';
  const [createFollowUp, setCreateFollowUp] = useState(isCompleted);
  const addComment = useAddComment(task.id, projectSlug);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    addComment.mutate(
      {
        body: body.trim(),
        triggers_continuation: isCompleted ? createFollowUp : false,
      },
      { onSuccess: () => setBody('') },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={
          isCompleted
            ? 'Add a comment. Tip: include "Remember: …" to save a project rule.'
            : 'Add a comment…'
        }
        rows={3}
        className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />

      {isCompleted && (
        <label
          className={`flex items-start gap-2 text-sm rounded border p-2 ${
            createFollowUp
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-slate-200 bg-slate-50 text-slate-600'
          }`}
        >
          <input
            type="checkbox"
            checked={createFollowUp}
            onChange={(e) => setCreateFollowUp(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">
              {createFollowUp
                ? 'This comment will create a follow-up task on the same branch.'
                : 'Plain comment only — no follow-up task will be created.'}
            </span>
            {createFollowUp && (
              <>
                <span className="block text-xs opacity-80 mt-0.5">
                  The pipeline will run <code className="font-mono">pipeline continue</code> against
                  the parent's branch (<code className="font-mono">{task.branch ?? '(none)'}</code>),
                  inheriting its plan + memory.
                </span>
                {task.task_summary && (
                  <span className="block text-xs mt-1.5 px-2 py-1.5 rounded bg-white/60 border border-emerald-200 text-emerald-900 whitespace-pre-wrap">
                    <span className="block font-semibold mb-0.5 uppercase tracking-wide opacity-70 text-[10px]">
                      Context the follow-up inherits
                    </span>
                    {task.task_summary.length > 320
                      ? `${task.task_summary.slice(0, 320)}…`
                      : task.task_summary}
                  </span>
                )}
              </>
            )}
          </span>
        </label>
      )}

      {addComment.error && (
        <div className="text-sm text-red-700">
          {(addComment.error as Error).message}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!body.trim() || addComment.isPending}
          className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white disabled:opacity-50"
        >
          {addComment.isPending
            ? 'Posting…'
            : createFollowUp && isCompleted
              ? 'Comment & create follow-up'
              : 'Comment'}
        </button>
      </div>
    </form>
  );
}
